import { Task, TaskPriority } from '../../types';

/**
 * Extended Task type with student name derived from case
 */
export interface DashboardTaskWithCase extends Task {
  studentName: string;
}

/**
 * Priority color mapping for visual indicators.
 * High: Red (#f44336), Medium: Orange (#ff9800), Low: Blue (#2196f3)
 * 
 * Requirements: 2.2
 */
export const priorityColors: Record<TaskPriority, string> = {
  high: '#f44336',
  medium: '#ff9800',
  low: '#2196f3',
};

/**
 * Get the color for a given priority level.
 * Returns distinct colors for each priority.
 * 
 * Requirements: 2.2
 */
export function getPriorityColor(priority: TaskPriority): string {
  return priorityColors[priority] ?? '#2196f3';
}

/**
 * Get numeric weight for priority level.
 * Higher weight = higher priority.
 * high: 3, medium: 2, low: 1
 */
export function getPriorityWeight(priority: TaskPriority): number {
  switch (priority) {
    case 'high':
      return 3;
    case 'medium':
      return 2;
    case 'low':
      return 1;
    default:
      return 0;
  }
}

/**
 * Sort tasks by due date (ascending) as primary sort,
 * then by priority (descending) as secondary sort for same due dates.
 * Tasks with null/undefined due dates are sorted to the end.
 *
 * Requirements: 3.1, 3.2
 */
export function sortTasksByDueDateAndPriority<T extends DashboardTaskWithCase>(
  tasks: T[]
): T[] {
  return [...tasks].sort((a, b) => {
    // Handle null/undefined due dates - always sort to end
    if (!a.dueDate && !b.dueDate) {
      // Both null: sort by priority descending
      return getPriorityWeight(b.priority) - getPriorityWeight(a.priority);
    }
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;

    // Primary sort: due date ascending
    const dateA = new Date(a.dueDate).getTime();
    const dateB = new Date(b.dueDate).getTime();

    if (dateA !== dateB) {
      return dateA - dateB;
    }

    // Secondary sort: priority descending (high before medium before low)
    return getPriorityWeight(b.priority) - getPriorityWeight(a.priority);
  });
}

/**
 * Filter out completed tasks, returning only incomplete tasks.
 * A task is incomplete if its status is not 'completed'.
 *
 * Requirements: 4.5
 */
export function filterIncompleteTasks<T extends DashboardTaskWithCase>(
  tasks: T[]
): T[] {
  return tasks.filter(task => task.status !== 'completed');
}
